import { IChatConversation } from 'src/@types/chat';
import { Doc, DocItem, DocTree } from 'src/@types/docs';
import { DocCommentsItem } from 'src/pages/dashboard/DocsPage/DocComments/types';
import { axios } from 'src/utils/axios';
import { UrlMetadataResult } from '../@types/url';
import { Roles } from '../features/workspace/WorkspaceShareMemberRoleSelectProps';

export async function getDocs(q?: string) {
  const { data } = await axios.get<DocItem[]>('/docs', {
    params: {
      q,
    },
  });
  return data;
}

export async function getDocById(id: string) {
  const { data } = await axios.get<Doc>(`/docs/${id}`);
  return data;
}

export async function createDoc(
  title: string,
  parent?: string,
  body = '',
): Promise<Doc> {
  const { data } = await axios.post<Doc>(`/docs`, {
    title,
    parent,
    body,
  });
  return data;
}

export type UpdateDocPayload = Partial<
  Omit<Doc, 'parent'> & { parent: string }
>;

export async function updateDoc(doc: UpdateDocPayload) {
  const { data } = await axios.put<Doc>(`/docs/${doc._id}`, doc);
  return data;
}

export async function deleteDocCover(id: Doc['_id']) {
  const { data } = await axios.delete(`/docs/cover/${id}`);
  return data;
}

export async function deleteDoc(id: Doc['_id']) {
  const { data } = await axios.delete(`/docs/${id}`);
  return data;
}

export type UpdateDocParticipants = {
  id: string;
  participants: string[];
};

export type UpdateParticipantRole = {
  id: string;
  participantId: string;
  role: Roles;
};

export async function addParticipantsAtDoc(payload: UpdateDocParticipants) {
  const { data } = await axios.put<Doc>(`/docs/participant/add`, payload);
  return data;
}

export async function favoriteDoc(docId: string) {
  const { data } = await axios.post(`/docs/favorite`, { docRef: docId });
  return data;
}

export async function unfavoriteDoc(docId: string) {
  const { data } = await axios.delete(`/docs/unfavorite/${docId}`);
  return data;
}

export async function removeParticipantsFromDoc(
  payload: UpdateDocParticipants,
) {
  const { data } = await axios.put<Doc>(`/docs/participant/remove`, payload);
  return data;
}

export async function leaveDoc(id: string) {
  const { data } = await axios.put('/docs/participant/leave', { id });
  return data;
}

export async function updateParticipantRole(payload: UpdateParticipantRole) {
  const { data } = await axios.put<Doc>(
    `/docs/participant/update/role`,
    payload,
  );
  return data;
}

export async function fetchDocumentComments(
  docId: string,
  params: { before?: string },
) {
  const { data } = await axios.get<{ items: DocCommentsItem[] }>(
    `/docs/comments/list/${docId}`,
    { params },
  );
  return data.items;
}

export async function getDocConversation(docId: string) {
  const { data } = await axios.get<IChatConversation>(
    `/docs/comments/room/${docId}`,
  );
  return data;
}

export type AddDocCommentPayload = Pick<
  DocCommentsItem,
  'content' | 'docId' | 'contentType' | 'mimeType' | 'fileName' | 'fileSize'
>;

export type EditDocCommentPayload = {
  content: string;
  messageId: string;
  docId: string;
};

export type RemoveDocCommentPayload = Pick<DocCommentsItem, '_id' | 'docId'>;

export type ReactDocCommentPayload = {
  reaction: string;
} & Pick<EditDocCommentPayload, 'docId' | 'messageId'>;

export type ReadDocCommentPayload = Pick<
  EditDocCommentPayload,
  'docId' | 'messageId'
>;

export async function createDocComment(payload: AddDocCommentPayload) {
  const { data } = await axios.post<DocCommentsItem>(
    `/docs/comments/send`,
    payload,
  );
  return data;
}

export async function editDocComment(payload: EditDocCommentPayload) {
  const { data } = await axios.put<DocCommentsItem>(
    `/docs/comments/edit`,
    payload,
  );
  return data;
}

export async function deleteDocComment({
  _id,
  docId,
}: RemoveDocCommentPayload) {
  const { data } = await axios.delete<DocCommentsItem>(
    `/docs/comments/delete?docId=${docId}&messageId=${_id}`,
  );
  return data;
}

export async function reactDocComment(payload: ReactDocCommentPayload) {
  const { data } = await axios.patch<DocCommentsItem>(
    `/docs/comments/reaction`,
    payload,
  );
  return data;
}

export async function readDocComment(payload: ReadDocCommentPayload) {
  const { data } = await axios.put<DocCommentsItem>(
    `/docs/comments/last-read`,
    payload,
  );
  return data;
}

export async function fetchUrlMetadata(url: string) {
  const { data } = await axios.get<UrlMetadataResult>(
    `/docs/metadata?url=${url}`,
  );
  return data;
}

export async function duplicateDoc(docId: string) {
  const { data } = await axios.post<Doc>('/docs/duplicate', { docId });
  return data;
}

export async function importDoc(file: File) {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await axios.post<Doc[]>('/docs/restore', fd);
  return data;
}

export async function exportDoc(docId?: string) {
  const { data } = await axios.get(`/docs/backup/${docId}`);
  return data;
}
type TrashDocsQuery = {
  q?: string;
  sort?: 'asc' | 'desc';
};

export async function getTrashDocs(query?: TrashDocsQuery) {
  const { data } = await axios.get<Doc[]>('/docs/trash', {
    params: query,
  });
  return data;
}

export async function retoreDocFromTrash(id: string) {
  const { data } = await axios.put<Doc>(`/docs/restore/${id}`);
  return data;
}

export async function makeDocPublic(id: string) {
  const { data } = await axios.put<Doc>('/docs/generate-access-token', {
    id,
  });
  return data;
}

export async function revokeDocPublic(id: string) {
  const { data } = await axios.post<Doc>('/docs/revoke-access-token', {
    id,
  });
  return data;
}

export async function getDocPublic(token: string) {
  const { data } = await axios.get<Doc>(`/docs/public?token=${token}`);
  return data;
}

export async function getDocsPublicTree(token: string) {
  const { data } = await axios.get<DocTree>(`/docs/public/tree?token=${token}`);
  return data;
}

export async function deleteDocFromTrash(id: string) {
  const { data } = await axios.delete<Doc>(`/docs/delete-permanently/${id}`);
  return data;
}

// export async function getDocs() {
//   const { data } = await axios.get<DocModel>(docsModule);
//   return data;
// }

// export async function getNestedDocs(docId: string) {
//   const { data } = await axios.get<TreeDoc[]>(`${docsModule}/nested-documents/${docId}`);
//   return data;
// }

// export async function getChildDocs(docId: string) {
//   const { data } = await axios.get<TreeDoc[]>(`${docsModule}/child-documents/${docId}`);
//   return data;
// }

// export async function getPublicChildDocs(docId: string) {
//   const { data } = await axios.get<TreeDoc[]>(`${docsModule}/public/child-documents/${docId}`);
//   return data;
// }

// export async function getPublicNestedDocs(docId: string) {
//   const { data } = await axios.get<{ treeDocs: TreeDoc[]; docs: Doc[] }>(
//     `${docsModule}/public/nested-documents/${docId}`
//   );
//   return data;
// }

// export async function getHomeData() {
//   const { data } = await axios.get<IHomeData>(`${docsModule}/home`);
//   return data;
// }

// export async function getSearchData(search: string) {
//   const { data } = await axios.get<Doc[]>(`${docsModule}/search/${search}`);
//   return data;
// }

// export async function getInTrashDocs() {
//   const { data } = await axios.get<Doc[]>(`${docsModule}/in-trash`);
//   return data;
// }

// export async function getDocById(id: string) {
//   const { data } = await axios.get<Doc>(`${docsModule}/${id}`);
//   return data;
// }

// export async function getHashUrl(id: string) {
//   const { data } = await axios.get<string>(`${docsModule}/hash/${id}`);
//   return data;
// }

// export async function getPublicDoc(hash: string, token: string) {
//   const { data } = await (token
//     ? axios.get<Doc>(`${docsModule}/${hash}/public`)
//     : axios.get<Doc>(`${docsModule}/public/${hash}`));
//   return data;
// }

// export async function getEmbedDoc(id: string) {
//   const { data } = await axios.get<Doc>(`${docsModule}/embed/${id}`);
//   return data;
// }

// export async function createDoc(title: string, parent?: string) {
//   const { data } = await axios.post<DocCreated>(docsModule, {
//     title,
//     parent,
//   });
//   return data;
// }

// export async function importDoc(formData: FormData) {
//   const { data } = await axios.post<string>(`${docsModule}/import`, formData);
//   return data;
// }

// export async function parseDoc(formData: FormData) {
//   const { data } = await axios.post<{ title: string; text: string }>(
//     `${docsModule}/parse-doc`,
//     formData
//   );
//   return data;
// }

// type updateType = Pick<Doc, 'title' | 'body' | 'lastView' | 'public'> | Doc;
// export async function updateDoc(
//   id: Doc['_id'],
//   doc: Partial<Omit<Doc, '_id' | 'parent'> & { parent: string }>
// ) {
//   const { data } = await axios.put<updateType>(`${docsModule}/${id}`, doc);
//   return data;
// }

// export async function updateGeneralDoc(
//   id: Doc['_id'],
//   doc: Partial<Omit<Doc, '_id' | 'parent'> & { parent: string }>
// ) {
//   const { data } = await axios.put<updateType>(`${docsModule}/general/${id}`, doc);
//   return data;
// }

// export async function shareDoc(_id: Doc['_id'], permissions: Doc['permissions']) {
//   const { data } = await axios.post<Doc>(`${docsModule}/share`, { _id, permissions });
//   return data;
// }

// export async function deleteDoc(id: Doc['_id']) {
//   const { data } = await axios.delete(`${docsModule}/${id}`);
//   return data;
// }

// export async function getUploadDocUrlFile(type: string) {
//   const { data } = await axios.post<{ url: string; key: string }>(`${docsModule}/file/getUrl`, {
//     type,
//   });
//   return data;
// }

// export async function uploadDocFile(url: string, file: Blob) {
//   const { data } = await axios.put(url, file, { headers: { 'Content-Type': file.type } });
//   return data;
// }

// export async function getUploadedFile({ key, fileName }: { key: string; fileName: string }) {
//   axios
//     .get<Blob>(`${docsModule}/file/getMedia/${key}`, {
//       responseType: 'blob',
//     })
//     .then((blob) => {
//       const url = window.URL.createObjectURL(blob.data);
//       const link = document.createElement('a');
//       link.href = url;
//       link.setAttribute('download', fileName);
//       document.body.appendChild(link);
//       link.click();
//       link.parentNode?.removeChild(link);
//     });
// }

export * as DocsRequests from './docs';
